import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TutorialRenderer } from "@/components/Tutorials/TutorialBlocks";
import NewsComments from "@/components/Community/NewsComments";
import { NewsPoll } from "@/components/community/NewsPoll";
import axios, { getApiUrl } from "@/lib/api";
import { useAuth } from "@/components/ProtectedRoute";
import { Users, Heart, Lightbulb, Newspaper, TrendingUp, Trophy, Star, Loader2, Target, Layers, Search, Filter, ChevronRight, Gamepad2, Clock, Zap, Flame, Skull, ListTodo, Copy, ClipboardList, MessageSquare, Edit2, BookOpen, Crown, Grid3X3, Eye, MapPin, Bug, AlertCircle, ArrowDownUp, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import PlatformBadge from "@/components/PlatformBadge";
import { toast } from "sonner";
import { FormattedText } from "@/components/ui/formatted-text";
import { EditCommunityItemDialog } from "@/components/MasterDatabase/CommunityDialogs";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { GuidelinesDialog } from "@/components/GuidelinesDialog";
import { AdBanner } from "@/components/AdSense";

const API = `${getApiUrl()}/api`;

const CATEGORY_LABELS = {
  developer_insight: "Developer Insight",
  easter_egg: "Easter Egg",
  historical_context: "Historical Context",
  pop_culture_reference: "Pop Culture Reference",
  speedrun_tip: "Speedrun Tip",
  lore: "Lore & Story",
  fun_fact: "Fun Fact",
  behind_the_scenes: "Behind the Scenes",
  other: "Other"
};

const DIFFICULTY_CONFIG = {
  easy: { label: "Easy", icon: Zap, color: "text-green-400", bg: "bg-green-500/20" },
  medium: { label: "Medium", icon: Target, color: "text-yellow-400", bg: "bg-yellow-500/20" },
  hard: { label: "Hard", icon: Flame, color: "text-orange-400", bg: "bg-orange-500/20" },
  insane: { label: "Insane", icon: Skull, color: "text-red-400", bg: "bg-red-500/20" }
};

const TRACKED_TYPES = ["news", "challenges", "organizers", "planners", "checklists", "grids", "maps", "trivia"];

export default function Social() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { paywallEnabled, isPro } = useSubscription();
  const [activeTab, setActiveTab] = useState("news");
  
  // Contributors state
  const [allContributors, setAllContributors] = useState({ trivia: [], challenges: [], organizers: [] });
  const [loadingContributors, setLoadingContributors] = useState(false);
  
  // Saved trivia state
  const [likedFacts, setLikedFacts] = useState([]);
  const [loadingLiked, setLoadingLiked] = useState(false);
  
  // Challenges state
  const [challenges, setChallenges] = useState([]);
  const [loadingChallenges, setLoadingChallenges] = useState(false);
  const [challengeSearch, setChallengeSearch] = useState("");
  const [challengeDifficulty, setChallengeDifficulty] = useState("all");
  
  // Organizers state
  const [organizers, setOrganizers] = useState([]);
  const [loadingOrganizers, setLoadingOrganizers] = useState(false);
  
  // Planners state
  const [planners, setPlanners] = useState([]);
  const [loadingPlanners, setLoadingPlanners] = useState(false);
  const [plannerSearch, setPlannerSearch] = useState("");
  const [plannerGameFilter, setPlannerGameFilter] = useState("");
  
  // Checklists state
  const [sharedChecklists, setSharedChecklists] = useState([]);
  const [loadingChecklists, setLoadingChecklists] = useState(false);
  const [checklistSearch, setChecklistSearch] = useState("");
  const [checklistCategory, setChecklistCategory] = useState("all");
  const [organizerSearch, setOrganizerSearch] = useState("");
  
  // Grids (templates) state
  const [communityGrids, setCommunityGrids] = useState([]);
  const [loadingGrids, setLoadingGrids] = useState(false);
  const [gridSearch, setGridSearch] = useState("");
  const [previewGrid, setPreviewGrid] = useState(null);
  
  // Maps state
  const [communityMaps, setCommunityMaps] = useState([]);
  const [loadingMaps, setLoadingMaps] = useState(false);
  const [mapSearch, setMapSearch] = useState("");
  
  // Game filter for challenges and organizers
  const [challengeGameFilter, setChallengeGameFilter] = useState("");
  const [organizerGameFilter, setOrganizerGameFilter] = useState("");
  
  // Tag filter state
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState("");
  
  // Edit community item state
  const [editItem, setEditItem] = useState(null);
  const [editItemType, setEditItemType] = useState(null);
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  
  // News/Updates state
  const [updates, setUpdates] = useState([]);
  const [loadingUpdates, setLoadingUpdates] = useState(false);

  // Community unread notifications (news + library-matched content)
  const [unreadIds, setUnreadIds] = useState({});
  const [showNewOnly, setShowNewOnly] = useState({});
  // Adoption filter (per adoptable tab): "all" | "adopted" | "unadopted".
  const ADOPTABLE_TYPES = ["challenges", "organizers", "planners", "checklists", "grids", "maps"];
  const [adoptedFilters, setAdoptedFilters] = useState({});
  const unreadCounts = useMemo(() => {
    const c = {};
    TRACKED_TYPES.forEach((t) => { c[t] = (unreadIds[t] || []).length; });
    return c;
  }, [unreadIds]);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/community/notifications/summary`);
      setUnreadIds(res.data?.unreadIds || {});
    } catch (e) { /* silent */ }
  }, []);

  const isNew = useCallback((type, id) => (unreadIds[type] || []).includes(id), [unreadIds]);

  // Filter a tab's items to unread-only when "Show new only" is enabled.
  // Falls back to showing all when there is nothing new (avoids an empty tab).
  const visible = useCallback((type, items) => {
    let list = items || [];
    // Adoption filter (only for adoptable tabs).
    const af = adoptedFilters[type];
    if (af && af !== "all") {
      list = list.filter((i) => (af === "adopted" ? i.isAdoptedByUser : !i.isAdoptedByUser));
    }
    if (!showNewOnly[type]) return list;
    const set = new Set(unreadIds[type] || []);
    if (set.size === 0) return list;
    return list.filter((i) => set.has(i.id));
  }, [showNewOnly, unreadIds, adoptedFilters]);

  const markRead = useCallback((type, id) => {
    setUnreadIds((prev) => {
      if (!(prev[type] || []).includes(id)) return prev;
      axios.post(`${API}/community/notifications/read`, { itemType: type, itemId: id })
        .then(() => window.dispatchEvent(new Event("community-unread-refresh")))
        .catch(() => {});
      return { ...prev, [type]: (prev[type] || []).filter((x) => x !== id) };
    });
  }, []);

  const markAllTypeRead = useCallback(async (type) => {
    setUnreadIds((prev) => ({ ...prev, [type]: [] }));
    try {
      await axios.post(`${API}/community/notifications/read-all`, { itemType: type });
      window.dispatchEvent(new Event("community-unread-refresh"));
    } catch (e) { /* silent */ }
  }, []);


  useEffect(() => { fetchUnread(); }, [fetchUnread]);
  useEffect(() => { fetchUnread(); }, [activeTab, fetchUnread]);
  
  // Trivia state
  const [triviaFacts, setTriviaFacts] = useState([]);
  const [loadingTrivia, setLoadingTrivia] = useState(false);
  const [triviaSearch, setTriviaSearch] = useState("");
  const [triviaCategory, setTriviaCategory] = useState("all");
  const [triviaSort, setTriviaSort] = useState("recent");

  // Bug reports & known issues state
  const [knownIssues, setKnownIssues] = useState([]);
  const [knownIssuesLoading, setKnownIssuesLoading] = useState(true);
  const [bugReportDialogOpen, setBugReportDialogOpen] = useState(false);
  const [bugReportSubmitting, setBugReportSubmitting] = useState(false);
  const [bugReport, setBugReport] = useState({
    title: "",
    description: "",
    category: "bug",
    severity: "medium",
    stepsToReproduce: "",
    expectedBehavior: "",
    actualBehavior: "",
  });

  // ---- Client-side pagination (per tab) ----
  const PAGE_SIZE_OPTIONS = [10, 25, 50];
  const [pages, setPages] = useState({});
  const [pageSizes, setPageSizes] = useState(() => {
    try { return JSON.parse(localStorage.getItem("communityPageSizes") || "{}") || {}; } catch { return {}; }
  });
  const sizeFor = (type) => pageSizes[type] || 10;
  // ---- Per-tab sorting (News / Grids / Maps) ----
  const [sortModes, setSortModes] = useState(() => {
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem("communitySortModes") || "{}") || {}; } catch { saved = {}; }
    return { news: "newest", grids: "newest", maps: "newest", ...saved };
  });
  const setSortMode = (type, mode) => {
    setSortModes((prev) => {
      const next = { ...prev, [type]: mode };
      try { localStorage.setItem("communitySortModes", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    setPages((prev) => ({ ...prev, [type]: 1 }));
  };
  const _itemTime = (x) => {
    const v = x.createdAt || x.submittedAt || x.publishedAt || x.updatedAt;
    const t = v ? new Date(v).getTime() : 0;
    return Number.isNaN(t) ? 0 : t;
  };
  const sortItems = (type, list) => {
    const mode = sortModes[type];
    if (!mode || !["news", "grids", "maps"].includes(type)) return list;
    const arr = [...list];
    if (type === "news") {
      // Pinned always first, then by date.
      arr.sort((a, b) => ((b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0)) || (mode === "oldest" ? _itemTime(a) - _itemTime(b) : _itemTime(b) - _itemTime(a)));
    } else if (mode === "oldest") {
      arr.sort((a, b) => _itemTime(a) - _itemTime(b));
    } else if (mode === "liked") {
      arr.sort((a, b) => (b.likesCount || 0) - (a.likesCount || 0));
    } else if (mode === "used") {
      arr.sort((a, b) => (b.useCount || 0) - (a.useCount || 0));
    } else {
      arr.sort((a, b) => _itemTime(b) - _itemTime(a));
    }
    return arr;
  };
  const SORT_OPTIONS = {
    news: [{ v: "newest", l: "Newest" }, { v: "oldest", l: "Oldest" }],
    grids: [{ v: "newest", l: "Newest" }, { v: "oldest", l: "Oldest" }, { v: "used", l: "Most used" }],
    maps: [{ v: "newest", l: "Newest" }, { v: "oldest", l: "Oldest" }, { v: "liked", l: "Most liked" }],
  };
  const setAdoptedFilter = (type, v) => {
    setAdoptedFilters((prev) => ({ ...prev, [type]: v }));
    setPages((prev) => ({ ...prev, [type]: 1 }));
  };
  const renderAdoptedFilter = (type) => (
    <Select value={adoptedFilters[type] || "all"} onValueChange={(v) => setAdoptedFilter(type, v)}>
      <SelectTrigger className="w-[150px] h-9 bg-[#0A0A0A] border-white/10" data-testid={`adopted-filter-${type}`}>
        <CheckCircle2 className="w-3.5 h-3.5 text-white/40 mr-1" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid={`adopted-filter-${type}-all`}>All items</SelectItem>
        <SelectItem value="adopted" data-testid={`adopted-filter-${type}-adopted`}>Adopted</SelectItem>
        <SelectItem value="unadopted" data-testid={`adopted-filter-${type}-unadopted`}>Not adopted</SelectItem>
      </SelectContent>
    </Select>
  );
  const renderSortSelect = (type) => (
    <Select value={sortModes[type] || "newest"} onValueChange={(v) => setSortMode(type, v)}>
      <SelectTrigger className="w-[150px] h-9 bg-[#0A0A0A] border-white/10" data-testid={`sort-${type}`}>
        <ArrowDownUp className="w-3.5 h-3.5 text-white/40 mr-1" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {(SORT_OPTIONS[type] || []).map((o) => (
          <SelectItem key={o.v} value={o.v} data-testid={`sort-${type}-${o.v}`}>{o.l}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
  const totalPagesFor = (type, items) => Math.max(1, Math.ceil(visible(type, items).length / sizeFor(type)));
  const currentPageFor = (type, items) => Math.min(Math.max(1, pages[type] || 1), totalPagesFor(type, items));
  const paged = (type, items) => {
    const list = sortItems(type, visible(type, items));
    const size = sizeFor(type);
    const p = Math.min(Math.max(1, pages[type] || 1), Math.max(1, Math.ceil(list.length / size)));
    const start = (p - 1) * size;
    return list.slice(start, start + size);
  };
  const goToPage = (type, p, items) => {
    const tp = totalPagesFor(type, items);
    setPages((prev) => ({ ...prev, [type]: Math.min(Math.max(1, p || 1), tp) }));
  };
  const setPageSize = (type, size) => {
    setPageSizes((prev) => {
      const next = { ...prev, [type]: size };
      try { localStorage.setItem("communityPageSizes", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    setPages((prev) => ({ ...prev, [type]: 1 }));
  };
  const renderPagination = (type, items) => {
    const total = visible(type, items).length;
    // Show controls (incl. the size selector) once there are more than the smallest page size.
    if (total <= PAGE_SIZE_OPTIONS[0]) return null;
    const size = sizeFor(type);
    const tp = Math.max(1, Math.ceil(total / size));
    const p = currentPageFor(type, items);
    return (
      <div className="flex flex-wrap items-center justify-center gap-3 pt-4" data-testid={`pagination-${type}`}>
        <div className="flex items-center gap-1.5">
          <span className="text-white/40 text-xs">Per page</span>
          <Select value={String(size)} onValueChange={(v) => setPageSize(type, parseInt(v, 10))}>
            <SelectTrigger className="w-[72px] h-8 bg-[#121212] border-white/10" data-testid={`pagination-${type}-size`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)} data-testid={`pagination-${type}-size-${n}`}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {tp > 1 && (
          <>
            <Button variant="outline" size="sm" disabled={p <= 1} onClick={() => goToPage(type, p - 1, items)} data-testid={`pagination-${type}-prev`} className="border-white/10">Prev</Button>
            <span className="text-white/60 text-sm" data-testid={`pagination-${type}-status`}>Page {p} of {tp}</span>
            <Button variant="outline" size="sm" disabled={p >= tp} onClick={() => goToPage(type, p + 1, items)} data-testid={`pagination-${type}-next`} className="border-white/10">Next</Button>
            <div className="flex items-center gap-1">
              <span className="text-white/40 text-xs">Jump to</span>
              <Input
                type="number"
                min={1}
                max={tp}
                defaultValue={p}
                key={`${type}-${p}-${tp}`}
                onKeyDown={(e) => { if (e.key === "Enter") { goToPage(type, parseInt(e.target.value, 10), items); e.target.blur(); } }}
                onBlur={(e) => { const v = parseInt(e.target.value, 10); if (v && v !== p) goToPage(type, v, items); }}
                className="w-16 h-8 text-center bg-[#121212] border-white/10"
                data-testid={`pagination-${type}-jump`}
              />
            </div>
          </>
        )}
      </div>
    );
  };
  // Reset to page 1 when a tab's search/filter changes (not on like/edit actions).
  useEffect(() => { setPages((p) => ({ ...p, challenges: 1 })); }, [challengeSearch, challengeDifficulty, challengeGameFilter]);
  useEffect(() => { setPages((p) => ({ ...p, organizers: 1 })); }, [organizerSearch, organizerGameFilter]);
  useEffect(() => { setPages((p) => ({ ...p, planners: 1 })); }, [plannerSearch, plannerGameFilter]);
  useEffect(() => { setPages((p) => ({ ...p, checklists: 1 })); }, [checklistSearch, checklistCategory]);
  useEffect(() => { setPages((p) => ({ ...p, grids: 1 })); }, [gridSearch]);
  useEffect(() => { setPages((p) => ({ ...p, maps: 1 })); }, [mapSearch]);
  useEffect(() => { setPages((p) => ({ ...p, trivia: 1 })); }, [triviaSearch, triviaCategory, triviaSort]);
  useEffect(() => { setPages((p) => ({ ...p, [activeTab]: 1 })); }, [showNewOnly]); // eslint-disable-line

  const fetchKnownIssues = async () => {
    setKnownIssuesLoading(true);
    try {
      const response = await axios.get(`${API}/known-issues`);
      setKnownIssues(response.data || []);
    } catch (error) {
      console.error("Failed to fetch known issues:", error);
    } finally {
      setKnownIssuesLoading(false);
    }
  };

  const handleSubmitBugReport = async () => {
    if (!bugReport.title.trim() || !bugReport.description.trim()) {
      toast.error("Please provide a title and description");
      return;
    }
    setBugReportSubmitting(true);
    try {
      await axios.post(`${API}/bug-reports`, bugReport);
      toast.success("Bug report submitted! Thank you for your feedback.");
      setBugReportDialogOpen(false);
      setBugReport({ title: "", description: "", category: "bug", severity: "medium", stepsToReproduce: "", expectedBehavior: "", actualBehavior: "" });
    } catch (error) {
      console.error("Failed to submit bug report:", error);
      toast.error(error.response?.data?.detail || "Failed to submit bug report");
    } finally {
      setBugReportSubmitting(false);
    }
  };

  useEffect(() => {
    if (activeTab === "bugs" && knownIssues.length === 0) {
      fetchKnownIssues();
    }
  }, [activeTab]);

  useEffect(() => {
    // Fetch available tags on mount
    axios.get(`${API}/community/tags`).then(res => {
      setAvailableTags(res.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === "news") {
      fetchUpdates();
    } else if (activeTab === "contributors") {
      fetchAllContributors();
    } else if (activeTab === "saved") {
      fetchLikedFacts();
    } else if (activeTab === "challenges") {
      fetchChallenges();
    } else if (activeTab === "organizers") {
      fetchOrganizers();
    } else if (activeTab === "planners") {
      fetchPlanners();
    } else if (activeTab === "checklists") {
      fetchChecklists();
    } else if (activeTab === "trivia") {
      fetchTrivia();
    } else if (activeTab === "grids") {
      fetchCommunityGrids();
    } else if (activeTab === "maps") {
      fetchCommunityMaps();
    }
  }, [activeTab]);

  const fetchUpdates = async () => {
    setLoadingUpdates(true);
    try {
      const response = await axios.get(`${API}/community/updates`);
      setUpdates(response.data || []);
    } catch (error) {
      console.error("Failed to fetch updates:", error);
    } finally {
      setLoadingUpdates(false);
    }
  };

  const fetchAllContributors = async () => {
    setLoadingContributors(true);
    try {
      const response = await axios.get(`${API}/community/contributors`);
      setAllContributors(response.data);
    } catch (error) {
      console.error("Failed to fetch contributors:", error);
    } finally {
      setLoadingContributors(false);
    }
  };

  const fetchLikedFacts = async () => {
    setLoadingLiked(true);
    try {
      const response = await axios.get(`${API}/achievement-facts/liked`);
      setLikedFacts(response.data.facts || []);
    } catch (error) {
      console.error("Failed to fetch liked facts:", error);
    } finally {
      setLoadingLiked(false);
    }
  };

  const fetchChallenges = useCallback(async () => {
    setLoadingChallenges(true);
    try {
      const params = new URLSearchParams();
      if (challengeSearch) params.append("search", challengeSearch);
      if (challengeDifficulty !== "all") params.append("difficulty", challengeDifficulty);
      if (challengeGameFilter) params.append("game", challengeGameFilter);
      if (selectedTag) params.append("tag", selectedTag);
      params.append("sort", "popular");
      params.append("limit", "50");
      
      const response = await axios.get(`${API}/community/challenges?${params.toString()}`);
      setChallenges(response.data.challenges || []);
    } catch (error) {
      console.error("Failed to fetch challenges:", error);
    } finally {
      setLoadingChallenges(false);
    }
  }, [challengeSearch, challengeDifficulty, challengeGameFilter, selectedTag]);

  const fetchOrganizers = useCallback(async () => {
    setLoadingOrganizers(true);
    try {
      const params = new URLSearchParams();
      if (organizerSearch) params.append("search", organizerSearch);
      if (organizerGameFilter) params.append("game", organizerGameFilter);
      if (selectedTag) params.append("tag", selectedTag);
      params.append("sort", "popular");
      params.append("limit", "50");
      
      const response = await axios.get(`${API}/community/organizers?${params.toString()}`);
      setOrganizers(response.data.organizers || []);
    } catch (error) {
      console.error("Failed to fetch organizers:", error);
    } finally {
      setLoadingOrganizers(false);
    }
  }, [organizerSearch, organizerGameFilter, selectedTag]);

  const fetchPlanners = useCallback(async () => {
    setLoadingPlanners(true);
    try {
      const params = new URLSearchParams();
      if (plannerSearch) params.append("search", plannerSearch);
      if (plannerGameFilter) params.append("game", plannerGameFilter);
      if (selectedTag) params.append("tag", selectedTag);
      params.append("sort", "popular");
      params.append("limit", "50");
      
      const response = await axios.get(`${API}/community/game-planners?${params.toString()}`);
      setPlanners(response.data.planners || []);
    } catch (error) {
      console.error("Failed to fetch planners:", error);
    } finally {
      setLoadingPlanners(false);
    }
  }, [plannerSearch, plannerGameFilter, selectedTag]);

  const fetchChecklists = useCallback(async () => {
    setLoadingChecklists(true);
    try {
      const params = new URLSearchParams();
      if (checklistSearch) params.append("search", checklistSearch);
      if (checklistCategory !== "all") params.append("category", checklistCategory);
      if (selectedTag) params.append("tag", selectedTag);
      params.append("limit", "50");
      
      const response = await axios.get(`${API}/community/checklists?${params.toString()}`);
      setSharedChecklists(response.data || []);
    } catch (error) {
      console.error("Failed to fetch checklists:", error);
    } finally {
      setLoadingChecklists(false);
    }
  }, [checklistSearch, checklistCategory, selectedTag]);

  const fetchCommunityGrids = useCallback(async () => {
    setLoadingGrids(true);
    try {
      const response = await axios.get(`${API}/scorecards/templates?public_only=true`);
      let grids = response.data || [];
      if (gridSearch) {
        const q = gridSearch.toLowerCase();
        grids = grids.filter(g => g.name?.toLowerCase().includes(q) || g.description?.toLowerCase().includes(q) || g.submittedByUsername?.toLowerCase().includes(q));
      }
      setCommunityGrids(grids);
    } catch (error) {
      console.error("Failed to fetch community grids:", error);
    } finally {
      setLoadingGrids(false);
    }
  }, [gridSearch]);

  const fetchCommunityMaps = useCallback(async () => {
    setLoadingMaps(true);
    try {
      const params = new URLSearchParams();
      if (mapSearch) params.append("search", mapSearch);
      params.append("sort", "popular");
      params.append("limit", "50");
      const response = await axios.get(`${API}/community/maps?${params.toString()}`);
      setCommunityMaps(response.data?.maps || []);
    } catch (error) {
      console.error("Failed to fetch community maps:", error);
    } finally {
      setLoadingMaps(false);
    }
  }, [mapSearch]);

  const fetchTrivia = useCallback(async () => {
    setLoadingTrivia(true);
    try {
      const params = new URLSearchParams();
      if (triviaSearch) params.append("search", triviaSearch);
      if (triviaCategory !== "all") params.append("category", triviaCategory);
      params.append("sort", triviaSort);
      params.append("limit", "50");
      
      const response = await axios.get(`${API}/achievement-facts/browse?${params.toString()}`);
      setTriviaFacts(response.data?.facts || []);
    } catch (error) {
      console.error("Failed to fetch trivia:", error);
    } finally {
      setLoadingTrivia(false);
    }
  }, [triviaSearch, triviaCategory, triviaSort]);

  const handleLikeTrivia = async (factId) => {
    try {
      const response = await axios.post(`${API}/achievement-facts/facts/${factId}/like`);
      setTriviaFacts(prev => prev.map(f => 
        f.id === factId ? { ...f, isLikedByUser: response.data.liked, likesCount: response.data.likesCount } : f
      ));
    } catch (error) {
      toast.error("Failed to like trivia");
    }
  };

  const handleUserEditSave = async (type, id, data) => {
    const endpointMap = {
      challenges: "challenges",
      organizers: "organizers",
      planners: "game-planners",
    };
    const endpoint = endpointMap[type];
    const res = await axios.put(`${API}/community/${endpoint}/${id}/edit`, data);
    if (res.data.editQueued) {
      toast.success("Edit submitted for admin review! Original stays live until approved.");
    } else {
      toast.success("Item updated!");
    }
    // Refresh the relevant list
    const refreshMap = {
      challenges: fetchChallenges,
      organizers: fetchOrganizers,
      planners: fetchPlanners,
    };
    refreshMap[type]?.();
  };



  // Refetch when filters change
  useEffect(() => {
    if (activeTab === "challenges") {
      const timer = setTimeout(fetchChallenges, 300);
      return () => clearTimeout(timer);
    }
  }, [challengeSearch, challengeDifficulty, challengeGameFilter, activeTab, fetchChallenges]);

  useEffect(() => {
    if (activeTab === "organizers") {
      const timer = setTimeout(fetchOrganizers, 300);
      return () => clearTimeout(timer);
    }
  }, [organizerSearch, organizerGameFilter, activeTab, fetchOrganizers]);

  useEffect(() => {
    if (activeTab === "planners") {
      const timer = setTimeout(fetchPlanners, 300);
      return () => clearTimeout(timer);
    }
  }, [plannerSearch, plannerGameFilter, activeTab, fetchPlanners]);

  useEffect(() => {
    if (activeTab === "checklists") {
      const timer = setTimeout(fetchChecklists, 300);
      return () => clearTimeout(timer);
    }
  }, [checklistSearch, checklistCategory, activeTab, fetchChecklists]);

  useEffect(() => {
    if (activeTab === "grids") {
      const timer = setTimeout(fetchCommunityGrids, 300);
      return () => clearTimeout(timer);
    }
  }, [gridSearch, activeTab, fetchCommunityGrids]);

  useEffect(() => {
    if (activeTab === "maps") {
      const timer = setTimeout(fetchCommunityMaps, 300);
      return () => clearTimeout(timer);
    }
  }, [mapSearch, activeTab, fetchCommunityMaps]);

  useEffect(() => {
    if (activeTab === "trivia") {
      const timer = setTimeout(fetchTrivia, 300);
      return () => clearTimeout(timer);
    }
  }, [triviaSearch, triviaCategory, triviaSort, activeTab, fetchTrivia]);

  const handleUnlike = async (factId) => {
    try {
      await axios.post(`${API}/achievement-facts/facts/${factId}/like`);
      setLikedFacts(likedFacts.filter(f => f.id !== factId));
    } catch (error) {
      console.error("Failed to unlike fact:", error);
    }
  };

  const handleLikeChallenge = async (challengeId) => {
    try {
      const response = await axios.post(`${API}/community/challenges/${challengeId}/like`);
      setChallenges(challenges.map(c => 
        c.id === challengeId 
          ? { ...c, isLikedByUser: response.data.liked, likesCount: response.data.likesCount }
          : c
      ));
    } catch (error) {
      console.error("Failed to like challenge:", error);
    }
  };

  const handleAdoptChallenge = async (challengeId) => {
    try {
      const response = await axios.post(`${API}/community/challenges/${challengeId}/adopt`);
      if (response.data.success) {
        toast.success(response.data.message);
        // Update adoption count
        setChallenges(challenges.map(c => 
          c.id === challengeId 
            ? { ...c, adoptedCount: (c.adoptedCount || 0) + 1, isAdoptedByUser: true }
            : c
        ));
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to adopt challenge");
    }
  };

  const handleLikeOrganizer = async (organizerId) => {
    try {
      const response = await axios.post(`${API}/community/organizers/${organizerId}/like`);
      setOrganizers(organizers.map(s => 
        s.id === organizerId 
          ? { ...s, isLikedByUser: response.data.liked, likesCount: response.data.likesCount }
          : s
      ));
    } catch (error) {
      console.error("Failed to like organizer:", error);
    }
  };

  // Adopt conflict state
  const [adoptConflict, setAdoptConflict] = useState(null); // {type: 'organizer'|'planner', id, existingSections, message}

  const handleAdoptOrganizer = async (organizerId, replace = false) => {
    try {
      const url = replace 
        ? `${API}/community/organizers/${organizerId}/adopt?replace=true`
        : `${API}/community/organizers/${organizerId}/adopt`;
      const response = await axios.post(url);
      if (response.data.success) {
        toast.success(response.data.message);
        setAdoptConflict(null);
        setOrganizers(organizers.map(s => 
          s.id === organizerId 
            ? { ...s, adoptedCount: (s.adoptedCount || 0) + 1, isAdoptedByUser: true }
            : s
        ));
      } else if (response.data.hasConflict) {
        setAdoptConflict({
          type: "organizer",
          id: organizerId,
          existingSections: response.data.existingSections,
          message: response.data.message
        });
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to adopt organizer");
    }
  };

  const handleLikePlanner = async (plannerId) => {
    try {
      const response = await axios.post(`${API}/community/game-planners/${plannerId}/like`);
      setPlanners(planners.map(p => 
        p.id === plannerId 
          ? { ...p, isLikedByUser: response.data.liked, likesCount: response.data.likesCount }
          : p
      ));
    } catch (error) {
      console.error("Failed to like planner:", error);
    }
  };

  const handleAdoptPlanner = async (plannerId, replace = false) => {
    try {
      const url = replace
        ? `${API}/community/game-planners/${plannerId}/adopt?replace=true`
        : `${API}/community/game-planners/${plannerId}/adopt`;
      const response = await axios.post(url);
      if (response.data.success) {
        toast.success(response.data.message);
        setAdoptConflict(null);
        setPlanners(planners.map(p => 
          p.id === plannerId 
            ? { ...p, adoptedCount: (p.adoptedCount || 0) + 1, isAdoptedByUser: true }
            : p
        ));
      } else if (response.data.hasConflict) {
        setAdoptConflict({
          type: "planner",
          id: plannerId,
          existingSections: response.data.existingSections,
          message: response.data.message
        });
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to adopt planner");
    }
  };

  // Adopt for Maps / Checklists / Grids — all save into the matching library game.
  // When the user doesn't own the game, offer to add it first (addGamePrompt).
  const [addGamePrompt, setAddGamePrompt] = useState(null); // {masterGameId, gameName, retry}

  const doAdopt = async (url, onSuccess) => {
    try {
      const { data } = await axios.post(url);
      if (data.success) {
        toast.success(data.message);
        onSuccess?.();
      } else if (data.needsGame && data.masterGameId) {
        setAddGamePrompt({ masterGameId: data.masterGameId, gameName: data.gameName, retry: () => doAdopt(url, onSuccess) });
      } else {
        toast.error(data.message || "Adoption failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to adopt");
    }
  };

  const handleAdoptMap = (id) =>
    doAdopt(`${API}/community/maps/${id}/adopt`, () =>
      setCommunityMaps((ms) => ms.map((m) => (m.id === id ? { ...m, adoptedCount: (m.adoptedCount || 0) + 1, isAdoptedByUser: true } : m)))
    );
  const handleAdoptChecklist = (id) =>
    doAdopt(`${API}/community/checklists/${id}/adopt`, () =>
      setSharedChecklists((cs) => cs.map((c) => (c.id === id ? { ...c, copies: (c.copies || 0) + 1, isAdoptedByUser: true } : c)))
    );
  const handleAdoptGrid = (id) =>
    doAdopt(`${API}/community/grids/${id}/adopt`, () =>
      setCommunityGrids((gs) => gs.map((g) => (g.id === id ? { ...g, useCount: (g.useCount || 0) + 1, isAdoptedByUser: true } : g)))
    );

  const handleAddGameAndAdopt = async () => {
    if (!addGamePrompt) return;
    const { masterGameId, gameName, retry } = addGamePrompt;
    try {
      await axios.post(`${API}/games/add-from-master`, { masterGameId });
      toast.success(`Added "${gameName}" to your library`);
      setAddGamePrompt(null);
      retry?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add game to library");
    }
  };

  const ContributorCard = ({ contributor, rank, type }) => {
    const isTop3 = rank < 3;
    const rankColors = ["text-yellow-400", "text-gray-300", "text-orange-400"];
    
    return (
      <div className="flex items-center justify-between p-3 bg-[#121212] rounded-lg border border-white/5">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
            isTop3 ? `${rankColors[rank]} bg-white/5` : "text-white/40 bg-white/5"
          }`}>
            {rank + 1}
          </div>
          <div>
            <p className="text-white font-medium text-sm">{contributor.username}</p>
            <div className="flex items-center gap-2 text-xs text-white/40">
              {type === "trivia" && (
                <>
                  <span className="text-purple-400">{contributor.approvedFacts} facts</span>
                  <span className="text-pink-400">{contributor.totalLikes} likes</span>
                </>
              )}
              {type === "challenges" && (
                <>
                  <span className="text-blue-400">{contributor.approvedChallenges} challenges</span>
                  <span className="text-green-400">{contributor.totalAdoptions} adoptions</span>
                </>
              )}
              {type === "organizers" && (
                <>
                  <span className="text-amber-400">{contributor.approvedOrganizers} organizers</span>
                  <span className="text-green-400">{contributor.totalAdoptions} adoptions</span>
                </>
              )}
            </div>
          </div>
        </div>
        {isTop3 && <Star className={`w-5 h-5 ${rankColors[rank]}`} />}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="social-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight">
            Community Items
          </h1>
          <p className="text-white/50 mt-1">Discover and share challenges, organizers, and trivia</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setGuidelinesOpen(true)}
          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
          data-testid="guidelines-btn"
        >
          <BookOpen className="w-4 h-4 mr-2" />
          Guidelines
        </Button>
      </div>

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#0A0A0A] border border-white/10 p-1 h-auto flex-wrap">
          <TabsTrigger value="news" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white flex items-center gap-2" data-testid="tab-news">
            <Newspaper className="w-4 h-4" />
            News
            {unreadCounts.news > 0 && (<span className="ml-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white" data-testid="tab-count-news">{unreadCounts.news}</span>)}
          </TabsTrigger>
          <TabsTrigger value="challenges" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white flex items-center gap-2" data-testid="tab-challenges">
            <Target className="w-4 h-4" />
            Challenges
            {unreadCounts.challenges > 0 && (<span className="ml-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white" data-testid="tab-count-challenges">{unreadCounts.challenges}</span>)}
          </TabsTrigger>
          <TabsTrigger value="organizers" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white flex items-center gap-2" data-testid="tab-organizers">
            <Layers className="w-4 h-4" />
            Organizers
            {unreadCounts.organizers > 0 && (<span className="ml-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white" data-testid="tab-count-organizers">{unreadCounts.organizers}</span>)}
          </TabsTrigger>
          <TabsTrigger value="planners" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white flex items-center gap-2" data-testid="tab-planners">
            <ClipboardList className="w-4 h-4" />
            Planners
            {unreadCounts.planners > 0 && (<span className="ml-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white" data-testid="tab-count-planners">{unreadCounts.planners}</span>)}
          </TabsTrigger>
          <TabsTrigger value="checklists" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white flex items-center gap-2" data-testid="tab-checklists">
            <ListTodo className="w-4 h-4" />
            Checklists
            {unreadCounts.checklists > 0 && (<span className="ml-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white" data-testid="tab-count-checklists">{unreadCounts.checklists}</span>)}
          </TabsTrigger>
          <TabsTrigger value="grids" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white flex items-center gap-2" data-testid="tab-grids">
            <Grid3X3 className="w-4 h-4" />
            Grids
            {unreadCounts.grids > 0 && (<span className="ml-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white" data-testid="tab-count-grids">{unreadCounts.grids}</span>)}
          </TabsTrigger>
          <TabsTrigger value="maps" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white flex items-center gap-2" data-testid="tab-maps">
            <MapPin className="w-4 h-4" />
            Maps
            {unreadCounts.maps > 0 && (<span className="ml-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white" data-testid="tab-count-maps">{unreadCounts.maps}</span>)}
          </TabsTrigger>
          <TabsTrigger value="trivia" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white flex items-center gap-2" data-testid="tab-trivia">
            <Lightbulb className="w-4 h-4" />
            Trivia
            {unreadCounts.trivia > 0 && (<span className="ml-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white" data-testid="tab-count-trivia">{unreadCounts.trivia}</span>)}
          </TabsTrigger>
          <TabsTrigger value="contributors" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white flex items-center gap-2" data-testid="tab-contributors">
            <Users className="w-4 h-4" />
            Contributors
          </TabsTrigger>
          <TabsTrigger value="saved" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white flex items-center gap-2" data-testid="tab-saved">
            <Heart className="w-4 h-4" />
            Saved
          </TabsTrigger>
          <TabsTrigger value="bugs" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white flex items-center gap-2" data-testid="tab-bugs">
            <Bug className="w-4 h-4" />
            Bugs & Issues
          </TabsTrigger>
        </TabsList>

        {TRACKED_TYPES.includes(activeTab) && (unreadCounts[activeTab] || 0) > 0 && (
          <div className="flex items-center justify-between mt-4 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="unread-banner">
            <span className="text-red-200 text-sm font-medium">
              {unreadCounts[activeTab]} new item{unreadCounts[activeTab] > 1 ? "s" : ""} in this section
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                className={`h-8 ${showNewOnly[activeTab] ? "text-white bg-red-500/25" : "text-red-300 hover:text-white hover:bg-red-500/20"}`}
                onClick={() => setShowNewOnly((prev) => ({ ...prev, [activeTab]: !prev[activeTab] }))}
                data-testid="show-new-only-toggle"
              >
                {showNewOnly[activeTab] ? "Showing new only" : "Show new only"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-red-300 hover:text-white hover:bg-red-500/20 h-8"
                onClick={() => markAllTypeRead(activeTab)}
                data-testid="mark-all-read-btn"
              >
                Mark all as read
              </Button>
            </div>
          </div>
        )}

        {/* News Tab */}
        <TabsContent value="news" className="mt-6">
          <Card className="bg-[#0A0A0A] border-white/5">
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-white flex items-center gap-2">
                  <Newspaper className="w-5 h-5 text-purple-400" />
                  Latest Updates
                </CardTitle>
                {updates.length > 1 && renderSortSelect("news")}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingUpdates ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                </div>
              ) : updates.length === 0 ? (
                <div className="text-center py-12">
                  <Newspaper className="w-12 h-12 mx-auto mb-4 text-white/20" />
                  <p className="text-white/50">No updates yet</p>
                  <p className="text-white/30 text-sm mt-1">Check back soon for news!</p>
                </div>
              ) : (
                <>
                {paged("news", updates).map((update) => {
                  const iconColors = {
                    purple: { bg: "bg-purple-500/20", text: "text-purple-400" },
                    blue: { bg: "bg-blue-500/20", text: "text-blue-400" },
                    green: { bg: "bg-green-500/20", text: "text-green-400" },
                    yellow: { bg: "bg-yellow-500/20", text: "text-yellow-400" },
                    red: { bg: "bg-red-500/20", text: "text-red-400" },
                    orange: { bg: "bg-orange-500/20", text: "text-orange-400" },
                  };
                  const colors = iconColors[update.iconColor] || iconColors.purple;
                  
                  const typeIcons = {
                    feature: TrendingUp,
                    announcement: Newspaper,
                    event: Clock,
                    maintenance: Zap,
                    tip: Lightbulb,
                  };
                  const IconComponent = typeIcons[update.updateType] || Newspaper;
                  
                  return (
                    <div 
                      key={update.id} 
                      onClick={() => markRead("news", update.id)}
                      className={`bg-[#121212] rounded-lg p-4 border transition-colors ${update.isPinned ? "border-purple-500/30" : "border-white/5"} ${isNew("news", update.id) ? "cursor-pointer ring-1 ring-red-500/40" : ""}`}
                      data-testid={`news-item-${update.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 ${colors.bg} rounded-lg`}>
                          <IconComponent className={`w-5 h-5 ${colors.text}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-white font-medium">{update.title}</h3>
                            {isNew("news", update.id) && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold" data-testid={`new-badge-news-${update.id}`}>NEW!</span>
                            )}
                            {update.isPinned && (
                              <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-300">Pinned</span>
                            )}
                          </div>
                          <p className="text-white/60 text-sm mt-1 whitespace-pre-wrap">{update.content}</p>
                          {Array.isArray(update.blocks) && update.blocks.length > 0 && (
                            <div className="mt-3" data-testid={`news-blocks-${update.id}`}>
                              <TutorialRenderer blocks={update.blocks} />
                            </div>
                          )}
                          {update.poll && update.poll.question && (
                            <NewsPoll
                              poll={update.poll}
                              updateId={update.id}
                              onPollUpdate={(newPoll) =>
                                setUpdates((prev) => prev.map((u) => (u.id === update.id ? { ...u, poll: newPoll } : u)))
                              }
                            />
                          )}
                          {Array.isArray(update.games) && update.games.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-3" data-testid={`news-games-${update.id}`}>
                              {update.games.map((g) => (
                                <Link
                                  key={g.id}
                                  to={`/browse/${g.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex gap-2.5 p-2 rounded-lg bg-black/30 border border-white/5 hover:border-purple-500/40 transition-colors group"
                                  data-testid={`news-game-${g.id}`}
                                >
                                  <div className="w-10 h-14 rounded bg-black/50 overflow-hidden shrink-0 flex items-center justify-center">
                                    {g.boxArtUrl ? (
                                      <img src={g.boxArtUrl} alt="" loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                    ) : (
                                      <Gamepad2 className="w-4 h-4 text-white/20" />
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-white text-xs font-medium line-clamp-2 group-hover:text-purple-300 transition-colors">{g.name}</p>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {g.system && <span className="text-[10px] px-1 py-px rounded bg-white/5 text-white/50">{g.system}</span>}
                                      {g.platform && g.platform !== g.system && <span className="text-[10px] px-1 py-px rounded bg-white/5 text-white/50">{g.platform}</span>}
                                      {g.region && <span className="text-[10px] px-1 py-px rounded bg-white/5 text-white/50">{g.region}</span>}
                                    </div>
                                    {g.releaseDate && <p className="text-[10px] text-white/35 mt-0.5">Released {g.releaseDate}</p>}
                                  </div>
                                </Link>
                              ))}
                            </div>
                          )}
                          {update.signature && (update.signature.name || update.signature.title || update.signature.tagline || update.signature.linkUrl) && (
                            <div className="mt-3 pt-3 border-t border-white/10 text-white/50" data-testid={`news-signature-${update.id}`}>
                              <p className="text-sm italic">
                                — {update.signature.name}
                                {update.signature.title ? <span className="text-white/40">, {update.signature.title}</span> : null}
                              </p>
                              {update.signature.tagline && (
                                <p className="text-xs text-white/40 mt-0.5">{update.signature.tagline}</p>
                              )}
                              {update.signature.linkUrl && (
                                <a
                                  href={update.signature.linkUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-xs text-purple-400 hover:text-purple-300 underline mt-0.5 inline-block"
                                  data-testid={`news-signature-link-${update.id}`}
                                >
                                  {update.signature.linkLabel || update.signature.linkUrl}
                                </a>
                              )}
                            </div>
                          )}
                          <p className="text-white/40 text-xs mt-2">{update.publishDate}</p>
                          <NewsComments updateId={update.id} initialCount={update.commentCount || 0} user={user} />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {renderPagination("news", updates)}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Challenges Tab */}
        <TabsContent value="challenges" className="mt-6 space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                value={challengeSearch}
                onChange={(e) => setChallengeSearch(e.target.value)}
                placeholder="Search challenges..."
                className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
            <div className="relative min-w-[200px]">
              <Gamepad2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                value={challengeGameFilter}
                onChange={(e) => setChallengeGameFilter(e.target.value)}
                placeholder="Filter by game..."
                className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
                data-testid="challenge-game-filter"
              />
            </div>
            <Select value={challengeDifficulty} onValueChange={setChallengeDifficulty}>
              <SelectTrigger className="w-[140px] bg-[#0A0A0A] border-white/10 text-white">
                <SelectValue placeholder="Difficulty" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="all">All Difficulties</SelectItem>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
                <SelectItem value="insane">Insane</SelectItem>
              </SelectContent>
            </Select>
            {renderAdoptedFilter("challenges")}
          </div>

          {/* Tag Filter */}
          {availableTags.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="challenge-tag-filter">
              <span className="text-xs text-white/40 flex-shrink-0">Tags:</span>
              <button
                onClick={() => { setSelectedTag(""); }}
                className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition-colors ${!selectedTag ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50' : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20'}`}
              >
                All
              </button>
              {availableTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => { setSelectedTag(selectedTag === tag ? "" : tag); }}
                  className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition-colors ${selectedTag === tag ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50' : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20'}`}
                  data-testid={`tag-filter-${tag}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          {/* Challenges Grid */}
          {loadingChallenges ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : challenges.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/5">
              <CardContent className="py-12 text-center">
                <Target className="w-12 h-12 mx-auto mb-4 text-white/20" />
                <p className="text-white/50">No challenges found</p>
                <p className="text-white/30 text-sm mt-1">Be the first to submit a challenge!</p>
              </CardContent>
            </Card>
          ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {paged("challenges", challenges).map((challenge) => {
                const diffConfig = DIFFICULTY_CONFIG[challenge.difficulty] || DIFFICULTY_CONFIG.medium;
                const DiffIcon = diffConfig.icon;
                
                return (
                  <Card key={challenge.id} onClick={() => markRead("challenges", challenge.id)} className={`bg-[#0A0A0A] border-white/5 hover:border-white/20 transition-colors ${isNew("challenges", challenge.id) ? "ring-1 ring-red-500/40" : ""}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {challenge.boxArtUrl ? (
                          <img src={challenge.boxArtUrl} alt={challenge.gameName} className="w-12 h-12 rounded object-contain bg-black/50 flex-shrink-0" />
                        ) : (
                          <div className={`p-2 rounded-lg ${diffConfig.bg} flex-shrink-0`}>
                            <DiffIcon className={`w-5 h-5 ${diffConfig.color}`} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-white font-medium">{challenge.name}</h3>
                            {isNew("challenges", challenge.id) && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold" data-testid={`new-badge-challenges-${challenge.id}`}>NEW!</span>
                            )}
                            <span className={`text-xs px-2 py-0.5 rounded ${diffConfig.bg} ${diffConfig.color}`}>
                              {diffConfig.label}
                            </span>
                            {challenge.hasPendingEdit && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">Edit pending</span>
                            )}
                            {challenge.isAdoptedByUser && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400 border border-green-500/30 font-semibold" data-testid={`adopted-badge-challenge-${challenge.id}`}>
                                <CheckCircle2 className="w-3 h-3" /> Adopted
                              </span>
                            )}
                          </div>
                          <p className="text-white/60 text-sm mt-1 line-clamp-2">{challenge.description}</p>
                          {challenge.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {challenge.tags.map(tag => (
                                <span key={tag} className="px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300/80 text-[10px] border border-purple-500/20">{tag}</span>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <PlatformBadge platform={challenge.gamePlatform} size="sm" />
                            <span className="text-white/40 text-xs">{challenge.gameName}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2 flex-wrap mt-3">
                            <div className="flex items-center gap-3 text-xs text-white/40 min-w-0 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Heart className="w-3 h-3" /> {challenge.likesCount || 0}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" /> {challenge.adoptedCount || 0} adopted
                              </span>
                              {challenge.showSubmitter && challenge.submittedByUsername && (
                                <span>by {challenge.submittedByUsername}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-auto">
                              {challenge.isOwner && !challenge.hasPendingEdit && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setEditItem(challenge); setEditItemType("challenges"); }}
                                  className="text-white/40 hover:text-cyan-400"
                                  data-testid={`edit-own-challenge-${challenge.id}`}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleLikeChallenge(challenge.id)}
                                className={challenge.isLikedByUser ? "text-pink-400" : "text-white/40"}
                              >
                                <Heart className={`w-4 h-4 ${challenge.isLikedByUser ? "fill-current" : ""}`} />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleAdoptChallenge(challenge.id)}
                                className="bg-purple-600 hover:bg-purple-500 text-white"
                              >
                                Adopt
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            {renderPagination("challenges", challenges)}
            </>
          )}
        </TabsContent>

        {/* Organizers Tab */}
        <TabsContent value="organizers" className="mt-6 space-y-4">
          {/* Search and Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                value={organizerSearch}
                onChange={(e) => setOrganizerSearch(e.target.value)}
                placeholder="Search organizers..."
                className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
            <div className="relative min-w-[200px]">
              <Gamepad2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                value={organizerGameFilter}
                onChange={(e) => setOrganizerGameFilter(e.target.value)}
                placeholder="Filter by game..."
                className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
                data-testid="organizer-game-filter"
              />
            </div>
            {renderAdoptedFilter("organizers")}
          </div>

          {/* Tag Filter */}
          {availableTags.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="organizer-tag-filter">
              <span className="text-xs text-white/40 flex-shrink-0">Tags:</span>
              <button
                onClick={() => { setSelectedTag(""); }}
                className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition-colors ${!selectedTag ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50' : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20'}`}
              >
                All
              </button>
              {availableTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => { setSelectedTag(selectedTag === tag ? "" : tag); }}
                  className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition-colors ${selectedTag === tag ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50' : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20'}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          {/* Organizers Grid */}
          {loadingOrganizers ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : organizers.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/5">
              <CardContent className="py-12 text-center">
                <Layers className="w-12 h-12 mx-auto mb-4 text-white/20" />
                <p className="text-white/50">No organizers found</p>
                <p className="text-white/30 text-sm mt-1">Be the first to share a organizer layout!</p>
              </CardContent>
            </Card>
          ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {paged("organizers", organizers).map((organizer) => (
                <Card key={organizer.id} onClick={() => markRead("organizers", organizer.id)} className={`bg-[#0A0A0A] border-white/5 hover:border-white/20 transition-colors ${isNew("organizers", organizer.id) ? "ring-1 ring-red-500/40" : ""}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {organizer.boxArtUrl ? (
                        <img src={organizer.boxArtUrl} alt={organizer.gameName} className="w-12 h-12 rounded object-contain bg-black/50 flex-shrink-0" />
                      ) : (
                        <div className="p-2 rounded-lg bg-amber-500/20 flex-shrink-0">
                          <Layers className="w-5 h-5 text-amber-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium">
                          {organizer.name}
                          {isNew("organizers", organizer.id) && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold" data-testid={`new-badge-organizers-${organizer.id}`}>NEW!</span>
                          )}
                          {organizer.hasPendingEdit && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-normal">Edit pending</span>
                          )}
                          {organizer.isAdoptedByUser && (
                            <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400 border border-green-500/30 font-semibold align-middle" data-testid={`adopted-badge-organizer-${organizer.id}`}>
                              <CheckCircle2 className="w-3 h-3" /> Adopted
                            </span>
                          )}
                        </h3>
                        {organizer.description && (
                          <p className="text-white/60 text-sm mt-1 line-clamp-2">{organizer.description}</p>
                        )}
                        {organizer.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {organizer.tags.map(tag => (
                              <span key={tag} className="px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300/80 text-[10px] border border-purple-500/20">{tag}</span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <PlatformBadge platform={organizer.gamePlatform} size="sm" />
                          <span className="text-white/40 text-xs">{organizer.gameName}</span>
                        </div>
                        <p className="text-amber-400/70 text-xs mt-1">
                          {organizer.achievementNames?.length || 0} achievements
                        </p>
                        <div className="flex items-center justify-between gap-2 flex-wrap mt-3">
                          <div className="flex items-center gap-3 text-xs text-white/40 min-w-0 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3" /> {organizer.likesCount || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" /> {organizer.adoptedCount || 0} adopted
                            </span>
                            {organizer.showSubmitter && organizer.submittedByUsername && (
                              <span>by {organizer.submittedByUsername}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-auto">
                            {organizer.isOwner && !organizer.hasPendingEdit && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setEditItem(organizer); setEditItemType("organizers"); }}
                                className="text-white/40 hover:text-cyan-400"
                                data-testid={`edit-own-organizer-${organizer.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleLikeOrganizer(organizer.id)}
                              className={organizer.isLikedByUser ? "text-pink-400" : "text-white/40"}
                            >
                              <Heart className={`w-4 h-4 ${organizer.isLikedByUser ? "fill-current" : ""}`} />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleAdoptOrganizer(organizer.id)}
                              className="bg-amber-600 hover:bg-amber-500 text-white"
                            >
                              Adopt
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {renderPagination("organizers", organizers)}
            </>
          )}
        </TabsContent>

        {/* Planners Tab */}
        <TabsContent value="planners" className="mt-6 space-y-4">
          {/* Search and Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                value={plannerSearch}
                onChange={(e) => setPlannerSearch(e.target.value)}
                placeholder="Search planners..."
                className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
                data-testid="planner-search"
              />
            </div>
            <div className="relative min-w-[200px]">
              <Gamepad2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                value={plannerGameFilter}
                onChange={(e) => setPlannerGameFilter(e.target.value)}
                placeholder="Filter by game..."
                className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
                data-testid="planner-game-filter"
              />
            </div>
            {renderAdoptedFilter("planners")}
          </div>

          {/* Tag Filter */}
          {availableTags.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="planner-tag-filter">
              <span className="text-xs text-white/40 flex-shrink-0">Tags:</span>
              <button
                onClick={() => { setSelectedTag(""); }}
                className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition-colors ${!selectedTag ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50' : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20'}`}
              >
                All
              </button>
              {availableTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => { setSelectedTag(selectedTag === tag ? "" : tag); }}
                  className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition-colors ${selectedTag === tag ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50' : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20'}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          {/* Planners Grid */}
          {loadingPlanners ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
            </div>
          ) : planners.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/5">
              <CardContent className="py-12 text-center">
                <ClipboardList className="w-12 h-12 mx-auto mb-4 text-white/20" />
                <p className="text-white/50">No planners found</p>
                <p className="text-white/30 text-sm mt-1">Be the first to share a game planner!</p>
              </CardContent>
            </Card>
          ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {paged("planners", planners).map((planner) => (
                <Card key={planner.id} onClick={() => markRead("planners", planner.id)} className={`bg-[#0A0A0A] border-white/5 hover:border-white/20 transition-colors ${isNew("planners", planner.id) ? "ring-1 ring-red-500/40" : ""}`} data-testid={`planner-card-${planner.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {planner.boxArtUrl ? (
                        <img src={planner.boxArtUrl} alt={planner.gameName} className="w-12 h-12 rounded object-contain bg-black/50 flex-shrink-0" />
                      ) : (
                        <div className="p-2 rounded-lg bg-indigo-500/20 flex-shrink-0">
                          <ClipboardList className="w-5 h-5 text-indigo-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium">
                          {planner.name}
                          {isNew("planners", planner.id) && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold" data-testid={`new-badge-planners-${planner.id}`}>NEW!</span>
                          )}
                          {planner.hasPendingEdit && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-normal">Edit pending</span>
                          )}
                          {planner.isAdoptedByUser && (
                            <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400 border border-green-500/30 font-semibold align-middle" data-testid={`adopted-badge-planner-${planner.id}`}>
                              <CheckCircle2 className="w-3 h-3" /> Adopted
                            </span>
                          )}
                        </h3>
                        {paywallEnabled && !isPro && (
                          <div className="flex items-center gap-1.5 mt-1 px-2 py-1 rounded bg-purple-500/10 border border-purple-500/20 w-fit">
                            <Crown className="w-3 h-3 text-purple-400" />
                            <span className="text-[10px] text-purple-300">Includes Pro features (achievement notes, checklists)</span>
                          </div>
                        )}
                        {planner.description && (
                          <p className="text-white/60 text-sm mt-1 line-clamp-2">{planner.description}</p>
                        )}
                        {planner.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {planner.tags.map(tag => (
                              <span key={tag} className="px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300/80 text-[10px] border border-purple-500/20">{tag}</span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <PlatformBadge platform={planner.gamePlatform} size="sm" />
                          <span className="text-white/40 text-xs">{planner.gameName}</span>
                        </div>
                        <p className="text-indigo-400/70 text-xs mt-1">
                          {planner.organizerData?.length || 0} organizer{(planner.organizerData?.length || 0) !== 1 ? "s" : ""}
                          {(planner.scorecardData?.length || 0) > 0 && (
                            <span className="text-emerald-400"> + {planner.scorecardData.length} grid{planner.scorecardData.length > 1 ? "s" : ""}</span>
                          )}
                        </p>
                        <div className="flex items-center justify-between gap-2 flex-wrap mt-3">
                          <div className="flex items-center gap-3 text-xs text-white/40 min-w-0 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3" /> {planner.likesCount || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" /> {planner.adoptedCount || 0} adopted
                            </span>
                            {planner.showSubmitter && planner.submittedByUsername && (
                              <span>by {planner.submittedByUsername}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-auto">
                            {planner.isOwner && !planner.hasPendingEdit && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setEditItem(planner); setEditItemType("planners"); }}
                                className="text-white/40 hover:text-cyan-400"
                                data-testid={`edit-own-planner-${planner.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleLikePlanner(planner.id)}
                              className={planner.isLikedByUser ? "text-pink-400" : "text-white/40"}
                              data-testid={`like-planner-${planner.id}`}
                            >
                              <Heart className={`w-4 h-4 ${planner.isLikedByUser ? "fill-current" : ""}`} />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleAdoptPlanner(planner.id)}
                              className="bg-indigo-600 hover:bg-indigo-500 text-white"
                              data-testid={`adopt-planner-${planner.id}`}
                            >
                              Adopt
                            </Button>
                            {paywallEnabled && !isPro && (
                              <span className="text-[9px] text-purple-400/60" title="Pro subscription required for full planner features">Pro</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {renderPagination("planners", planners)}
            </>
          )}
        </TabsContent>

        {/* Checklists Tab */}
        <TabsContent value="checklists" className="mt-6 space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                value={checklistSearch}
                onChange={(e) => setChecklistSearch(e.target.value)}
                placeholder="Search checklists..."
                className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
                data-testid="checklist-search"
              />
            </div>
            <Select value={checklistCategory} onValueChange={setChecklistCategory}>
              <SelectTrigger className="w-[160px] bg-[#0A0A0A] border-white/10 text-white" data-testid="checklist-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="collectibles">Collectibles</SelectItem>
                <SelectItem value="missables">Missables</SelectItem>
                <SelectItem value="speedrun">Speedrun</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            {renderAdoptedFilter("checklists")}
          </div>

          {/* Checklists Grid */}
          {loadingChecklists ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : sharedChecklists.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/5">
              <CardContent className="py-12 text-center">
                <ListTodo className="w-12 h-12 mx-auto mb-4 text-white/20" />
                <p className="text-white/50">No checklists found</p>
                <p className="text-white/30 text-sm mt-1">Share your checklists from a game page!</p>
              </CardContent>
            </Card>
          ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {paged("checklists", sharedChecklists).map((checklist) => (
                <Card key={checklist.id} onClick={() => markRead("checklists", checklist.id)} className={`bg-[#0A0A0A] border-white/5 hover:border-white/20 transition-colors ${isNew("checklists", checklist.id) ? "ring-1 ring-red-500/40" : ""}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-teal-500/20">
                        <ListTodo className="w-5 h-5 text-teal-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-white font-medium">{checklist.title}</h3>
                          {isNew("checklists", checklist.id) && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold" data-testid={`new-badge-checklists-${checklist.id}`}>NEW!</span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            checklist.category === 'collectibles' ? 'bg-amber-500/20 text-amber-400' :
                            checklist.category === 'missables' ? 'bg-red-500/20 text-red-400' :
                            checklist.category === 'speedrun' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-purple-500/20 text-purple-400'
                          }`}>{checklist.category}</span>
                          {checklist.isAdoptedByUser && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400 border border-green-500/30 font-semibold" data-testid={`adopted-badge-checklist-${checklist.id}`}>
                              <CheckCircle2 className="w-3 h-3" /> Adopted
                            </span>
                          )}
                        </div>
                        {checklist.description && (
                          <p className="text-white/60 text-sm mt-1 line-clamp-2">{checklist.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Gamepad2 className="w-3 h-3 text-white/40" />
                          <span className="text-white/40 text-xs">{checklist.gameName || "Unknown Game"}</span>
                        </div>
                        <p className="text-teal-400/70 text-xs mt-1">
                          {checklist.itemCount || checklist.items?.length || 0} items
                        </p>
                        <div className="flex items-center justify-between gap-2 flex-wrap mt-3">
                          <div className="flex items-center gap-3 text-xs text-white/40 min-w-0 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Copy className="w-3 h-3" /> {checklist.copies || 0} copies
                            </span>
                            {checklist.authorName && (
                              <span>by {checklist.authorName}</span>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleAdoptChecklist(checklist.id); }}
                            className="bg-teal-600 hover:bg-teal-500 text-white shrink-0 ml-auto"
                            data-testid={`adopt-checklist-${checklist.id}`}
                          >
                            Adopt
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {renderPagination("checklists", sharedChecklists)}
            </>
          )}
        </TabsContent>


        {/* Grids Tab */}
        <TabsContent value="grids" className="mt-6 space-y-4" data-testid="grids-tab-content">
          {/* Search */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                value={gridSearch}
                onChange={(e) => setGridSearch(e.target.value)}
                placeholder="Search grids by name or author..."
                className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
                data-testid="grid-search"
              />
            </div>
            {renderSortSelect("grids")}
            {renderAdoptedFilter("grids")}
          </div>

          <p className="text-white/40 text-sm">
            Browse community-shared grid templates. Use them in any game to track scores, collectibles, and more.
          </p>

          {/* Grids List */}
          {loadingGrids ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : communityGrids.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/5">
              <CardContent className="py-12 text-center">
                <Grid3X3 className="w-12 h-12 mx-auto mb-4 text-white/20" />
                <p className="text-white/50">No community grids yet</p>
                <p className="text-white/30 text-sm mt-1">Create a grid on any game page and share it as a public template!</p>
              </CardContent>
            </Card>
          ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paged("grids", communityGrids).map((grid) => (
                <Card key={grid.id} onClick={() => markRead("grids", grid.id)} className={`bg-[#0A0A0A] border-white/5 hover:border-purple-500/30 transition-colors group ${isNew("grids", grid.id) ? "ring-1 ring-red-500/40" : ""}`} data-testid={`community-grid-${grid.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-purple-500/15 flex-shrink-0">
                        <Grid3X3 className="w-5 h-5 text-purple-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium text-sm truncate">{grid.name}</h3>
                        {grid.isAdoptedByUser && (
                          <span className="inline-flex items-center gap-0.5 mt-0.5 text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400 border border-green-500/30 font-semibold" data-testid={`adopted-badge-grid-${grid.id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Adopted
                          </span>
                        )}
                        {isNew("grids", grid.id) && (
                          <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold" data-testid={`new-badge-grids-${grid.id}`}>NEW!</span>
                        )}
                        {grid.description && (
                          <p className="text-white/50 text-xs mt-0.5 line-clamp-2">{grid.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-white/40">
                          <span>{grid.rows?.length || 0} rows x {grid.cols?.length || 0} cols</span>
                          <span>Used {grid.useCount || 0}x</span>
                        </div>
                        {grid.submittedByUsername && (
                          <p className="text-white/30 text-xs mt-1">by {grid.submittedByUsername}</p>
                        )}
                      </div>
                    </div>
                    {/* Mini preview of the grid structure */}
                    {grid.rows?.length > 0 && grid.cols?.length > 0 && (
                      <div className="mt-3 bg-zinc-900/60 rounded p-2 overflow-hidden">
                        <div className="flex gap-0.5">
                          {grid.cols.slice(0, 6).map(c => (
                            <div key={c.id} className="flex-1 min-w-0">
                              {c.isDivider ? (
                                <div className="w-[1px] h-3 mx-auto rounded" style={{ backgroundColor: c.color || "#3f3f46" }} />
                              ) : (
                                <div className="text-[8px] text-zinc-500 text-center truncate px-0.5">{c.name || ""}</div>
                              )}
                            </div>
                          ))}
                          {grid.cols.length > 6 && <div className="text-[8px] text-zinc-600">+{grid.cols.length - 6}</div>}
                        </div>
                        {grid.rows.slice(0, 4).map(r => (
                          <div key={r.id} className="flex gap-0.5 mt-0.5">
                            {r.isDivider ? (
                              <div className="flex-1 h-[1px] rounded my-0.5" style={{ backgroundColor: r.color || "#3f3f46" }} />
                            ) : (
                              <>
                                {grid.cols.slice(0, 6).map(c => {
                                  if (c.isDivider) return <div key={c.id} className="w-[1px] bg-zinc-700 mx-0.5" />;
                                  const cell = grid.cellDefaults?.[`${r.id}:${c.id}`];
                                  const type = cell?.type || "checkbox";
                                  return (
                                    <div key={c.id} className="flex-1 h-4 rounded border border-zinc-800 bg-zinc-900/40 flex items-center justify-center">
                                      <span className={`text-[8px] ${type === "checkbox" ? (cell?.value ? "text-purple-400" : "text-zinc-700") : type === "number" ? "text-emerald-400" : "text-amber-400"}`}>
                                        {type === "checkbox" ? (cell?.value ? "\u2713" : "\u2610") : type === "number" ? (cell?.value || "0") : (cell?.value || "\u00B7")}
                                      </span>
                                    </div>
                                  );
                                })}
                              </>
                            )}
                          </div>
                        ))}
                        {grid.rows.length > 4 && <p className="text-[8px] text-zinc-600 text-center mt-0.5">+{grid.rows.length - 4} more</p>}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPreviewGrid(grid)}
                        className="text-zinc-400 hover:text-white h-7 text-xs flex-1"
                        data-testid={`preview-grid-${grid.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" /> Preview
                      </Button>
                      <Button
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleAdoptGrid(grid.id); }}
                        className="bg-purple-600 hover:bg-purple-500 text-white h-7 text-xs flex-1"
                        data-testid={`adopt-grid-${grid.id}`}
                      >
                        Adopt
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {renderPagination("grids", communityGrids)}
            </>
          )}
        </TabsContent>

        {/* Maps Tab */}
        <TabsContent value="maps" className="mt-6 space-y-4" data-testid="maps-tab-content">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                placeholder="Search maps by title, game, or description..."
                value={mapSearch}
                onChange={(e) => setMapSearch(e.target.value)}
                className="pl-10 bg-black/50 border-white/10 text-white"
                data-testid="map-search-input"
              />
            </div>
            {renderSortSelect("maps")}
            {renderAdoptedFilter("maps")}
          </div>

          {loadingMaps ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
            </div>
          ) : communityMaps.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-16 h-16 mx-auto mb-4 text-white/10" />
              <p className="text-white/50 text-lg">No community maps yet</p>
              <p className="text-white/30 text-sm mt-1">Be the first to submit a map from a game!</p>
            </div>
          ) : (
            <>
            <div className="grid gap-4 sm:grid-cols-2">
              {paged("maps", communityMaps).map((map) => (
                <Card key={map.id} onClick={() => markRead("maps", map.id)} className={`bg-[#0A0A0A] border-white/5 overflow-hidden ${isNew("maps", map.id) ? "ring-1 ring-red-500/40" : ""}`} data-testid={`social-map-${map.id}`}>
                  {map.mapImageUrl && (
                    <div className="w-full h-32 overflow-hidden border-b border-white/5">
                      <img src={map.mapImageUrl} alt={map.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-white font-medium text-sm flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                          {map.title}
                          {isNew("maps", map.id) && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold" data-testid={`new-badge-maps-${map.id}`}>NEW!</span>
                          )}
                          {map.isAdoptedByUser && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400 border border-green-500/30 font-semibold" data-testid={`adopted-badge-map-${map.id}`}>
                              <CheckCircle2 className="w-3 h-3" /> Adopted
                            </span>
                          )}
                        </h3>
                        <p className="text-white/50 text-xs mt-0.5">{map.gameName} ({map.gamePlatform})</p>
                      </div>
                    </div>
                    {map.description && (
                      <p className="text-white/40 text-xs line-clamp-2">{map.description}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-white/30">
                      <span>{(map.pins || []).length} pins</span>
                      {map.sectionName && <span>Section: {map.sectionName}</span>}
                      <span>Adopted {map.adoptedCount || 0}x</span>
                      <span className="flex items-center gap-1">
                        <Heart className={`w-3 h-3 ${map.isLikedByUser ? "fill-red-400 text-red-400" : ""}`} />
                        {map.likesCount || 0}
                      </span>
                    </div>
                    {(map.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {map.tags.map(t => (
                          <span key={t} className="text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full">{t}</span>
                        ))}
                      </div>
                    )}
                    {map.showSubmitter && map.submittedByUsername && (
                      <p className="text-white/20 text-xs">Submitted by {map.submittedByUsername}</p>
                    )}
                    <div className="flex justify-end pt-1">
                      <Button
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleAdoptMap(map.id); }}
                        className="bg-amber-600 hover:bg-amber-500 text-white"
                        data-testid={`adopt-map-${map.id}`}
                      >
                        Adopt
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {renderPagination("maps", communityMaps)}
            </>
          )}
        </TabsContent>

        {/* Trivia Tab */}
        <TabsContent value="trivia" className="mt-6 space-y-4">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                placeholder="Search trivia by achievement, game, or content..."
                value={triviaSearch}
                onChange={(e) => setTriviaSearch(e.target.value)}
                className="pl-10 bg-black/50 border-white/10 text-white"
                data-testid="trivia-search-input"
              />
            </div>
            <Select value={triviaCategory} onValueChange={setTriviaCategory}>
              <SelectTrigger className="w-[180px] bg-black/50 border-white/10" data-testid="trivia-category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={triviaSort} onValueChange={setTriviaSort}>
              <SelectTrigger className="w-[140px] bg-black/50 border-white/10" data-testid="trivia-sort-filter">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
                <SelectItem value="popular">Popular</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loadingTrivia ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : triviaFacts.length === 0 ? (
            <div className="text-center py-12 text-white/50">
              <Lightbulb className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No trivia found. Be the first to submit some!</p>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {paged("trivia", triviaFacts).map(fact => (
                <Card key={fact.id} onClick={() => markRead("trivia", fact.id)} className={`bg-[#0A0A0A] border-white/5 hover:border-white/10 transition-colors ${isNew("trivia", fact.id) ? "ring-1 ring-red-500/40" : ""}`} data-testid={`trivia-card-${fact.id}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      {fact.gameBoxArtUrl && (
                        <img 
                          src={fact.gameBoxArtUrl} 
                          alt={fact.gameName}
                          className="w-12 h-12 rounded object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-white/60 text-xs">{fact.gameName}</p>
                            <p className="text-white font-medium text-sm truncate">{fact.achievementName}
                              {isNew("trivia", fact.id) && (
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold" data-testid={`new-badge-trivia-${fact.id}`}>NEW!</span>
                              )}
                            </p>
                          </div>
                          {fact.category && CATEGORY_LABELS[fact.category] && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 whitespace-nowrap">
                              {CATEGORY_LABELS[fact.category]}
                            </span>
                          )}
                        </div>
                        <div className="mt-2">
                          <FormattedText text={fact.factText} className="text-white/80 text-sm leading-relaxed" />
                        </div>
                        <div className="flex items-center gap-3 mt-3">
                          <button
                            onClick={() => handleLikeTrivia(fact.id)}
                            className={`flex items-center gap-1 text-xs transition-colors ${
                              fact.isLikedByUser ? "text-red-400" : "text-white/40 hover:text-red-400"
                            }`}
                            data-testid={`trivia-like-${fact.id}`}
                          >
                            <Heart className={`w-3.5 h-3.5 ${fact.isLikedByUser ? "fill-current" : ""}`} />
                            {fact.likesCount || 0}
                          </button>
                          {fact.submittedByUsername && fact.showSubmitter && (
                            <span className="text-white/30 text-xs flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {fact.submittedByUsername}
                            </span>
                          )}
                          {fact.sourceUrl && (
                            <a 
                              href={fact.sourceUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-purple-400/60 hover:text-purple-400 text-xs"
                            >
                              Source
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {renderPagination("trivia", triviaFacts)}
            </>
          )}
        </TabsContent>

        {/* Contributors Tab */}
        <TabsContent value="contributors" className="mt-6">
          {loadingContributors ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Trivia Contributors */}
              <Card className="bg-[#0A0A0A] border-white/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-purple-400" />
                    Top Trivia Contributors
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {allContributors.trivia?.length === 0 ? (
                    <p className="text-white/40 text-sm text-center py-4">No contributors yet</p>
                  ) : (
                    allContributors.trivia?.map((c, i) => (
                      <ContributorCard key={c.userId} contributor={c} rank={i} type="trivia" />
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Challenge Contributors */}
              <Card className="bg-[#0A0A0A] border-white/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Target className="w-4 h-4 text-blue-400" />
                    Top Challenge Creators
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {allContributors.challenges?.length === 0 ? (
                    <p className="text-white/40 text-sm text-center py-4">No contributors yet</p>
                  ) : (
                    allContributors.challenges?.map((c, i) => (
                      <ContributorCard key={c.userId} contributor={c} rank={i} type="challenges" />
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Organizer Contributors */}
              <Card className="bg-[#0A0A0A] border-white/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Layers className="w-4 h-4 text-amber-400" />
                    Top Organizer Curators
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {allContributors.organizers?.length === 0 ? (
                    <p className="text-white/40 text-sm text-center py-4">No contributors yet</p>
                  ) : (
                    allContributors.organizers?.map((c, i) => (
                      <ContributorCard key={c.userId} contributor={c} rank={i} type="organizers" />
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Saved Trivia Tab */}
        <TabsContent value="saved" className="mt-6">
          <Card className="bg-[#0A0A0A] border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Heart className="w-5 h-5 text-pink-400" />
                Your Saved Trivia
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingLiked ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                </div>
              ) : likedFacts.length === 0 ? (
                <div className="text-center py-12">
                  <Heart className="w-12 h-12 mx-auto mb-4 text-white/20" />
                  <p className="text-white/50">No saved trivia yet</p>
                  <p className="text-white/30 text-sm mt-1">Like facts on game pages to save them here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {likedFacts.map((fact) => (
                    <div 
                      key={fact.id}
                      className="bg-[#121212] rounded-lg p-4 border border-white/5"
                    >
                      <div className="flex items-start gap-3">
                        {fact.achievementImageUrl ? (
                          <img 
                            src={fact.achievementImageUrl} 
                            alt=""
                            className="w-12 h-12 rounded object-contain bg-black/50"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded bg-purple-500/20 flex items-center justify-center">
                            <Lightbulb className="w-6 h-6 text-purple-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-300">
                              {CATEGORY_LABELS[fact.category] || fact.category}
                            </span>
                            {fact.gamePlatform && (
                              <PlatformBadge platform={fact.gamePlatform} size="sm" />
                            )}
                          </div>
                          <p className="text-white mt-2">{fact.factText}</p>
                          <div className="flex items-center justify-between mt-3">
                            <Link 
                              to={`/games/${fact.masterGameId}`}
                              className="text-white/60 hover:text-white text-sm"
                            >
                              {fact.gameName} - {fact.achievementName}
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUnlike(fact.id)}
                              className="text-pink-400 hover:text-pink-300 hover:bg-pink-500/10"
                            >
                              <Heart className="w-4 h-4 mr-1 fill-current" />
                              Unlike
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bugs & Issues Tab */}
        <TabsContent value="bugs" className="mt-6 space-y-4" data-testid="bugs-tab-content">
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <h3 className="text-white font-medium flex items-center gap-2 mb-2">
              <MessageSquare className="w-4 h-4 text-purple-400" />
              Report a Bug
            </h3>
            <p className="text-white/50 text-sm mb-4">
              Found something not working right? Let us know!
            </p>
            <Button onClick={() => setBugReportDialogOpen(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="open-bug-report-btn">
              <Bug className="w-4 h-4 mr-2" />
              Submit Bug Report
            </Button>
          </div>

          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <h3 className="text-white font-medium flex items-center gap-2 mb-4">
              <AlertCircle className="w-4 h-4 text-amber-400" />
              Known Issues
            </h3>
            {knownIssuesLoading ? (
              <div className="flex items-center gap-2 text-white/50">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </div>
            ) : knownIssues.length === 0 ? (
              <p className="text-white/50 text-sm">No known issues at this time.</p>
            ) : (
              <div className="space-y-3" data-testid="known-issues-list">
                {knownIssues.map((issue) => (
                  <div key={issue.id} className="p-3 bg-black/30 rounded-lg border border-white/5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <h4 className="text-white font-medium text-sm">{issue.title}</h4>
                        <p className="text-white/60 text-xs mt-1">{issue.description}</p>
                        {issue.workaround && (
                          <p className="text-green-400/80 text-xs mt-2">
                            <strong>Workaround:</strong> {issue.workaround}
                          </p>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        issue.status === 'fixed' ? 'bg-green-500/20 text-green-400' :
                        issue.status === 'fix_in_progress' ? 'bg-blue-500/20 text-blue-400' :
                        issue.status === 'investigating' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-white/10 text-white/60'
                      }`}>
                        {issue.status === 'fix_in_progress' ? 'Fix in Progress' :
                         issue.status.charAt(0).toUpperCase() + issue.status.slice(1)}
                      </span>
                    </div>
                    {issue.affectedAreas?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {issue.affectedAreas.map((area, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-white/5 text-white/40 text-xs rounded">
                            {area}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Bug Report Dialog */}
      <Dialog open={bugReportDialogOpen} onOpenChange={setBugReportDialogOpen}>
        <DialogContent className="sm:max-w-[550px] bg-[#0A0A0A] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Bug className="w-5 h-5 text-purple-500" />
              Submit Bug Report
            </DialogTitle>
            <DialogDescription>
              Help us improve by reporting any issues you encounter
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <label className="text-sm text-white/70">Title *</label>
              <Input value={bugReport.title} onChange={(e) => setBugReport({...bugReport, title: e.target.value})} placeholder="Brief summary of the issue" className="bg-black/50 border-white/10 text-white" data-testid="bug-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-white/70">Category</label>
                <Select value={bugReport.category} onValueChange={(v) => setBugReport({...bugReport, category: v})}>
                  <SelectTrigger className="bg-black/50 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="feature">Feature Request</SelectItem>
                    <SelectItem value="question">Question</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-white/70">Severity</label>
                <Select value={bugReport.severity} onValueChange={(v) => setBugReport({...bugReport, severity: v})}>
                  <SelectTrigger className="bg-black/50 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/70">Description *</label>
              <Textarea value={bugReport.description} onChange={(e) => setBugReport({...bugReport, description: e.target.value})} placeholder="Describe the issue in detail" className="bg-black/50 border-white/10 text-white min-h-[100px]" data-testid="bug-description-input" />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/70">Steps to Reproduce</label>
              <Textarea value={bugReport.stepsToReproduce} onChange={(e) => setBugReport({...bugReport, stepsToReproduce: e.target.value})} placeholder="1. Go to...&#10;2. Click on...&#10;3. See error" className="bg-black/50 border-white/10 text-white min-h-[80px]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-white/70">Expected Behavior</label>
                <Textarea value={bugReport.expectedBehavior} onChange={(e) => setBugReport({...bugReport, expectedBehavior: e.target.value})} placeholder="What should happen" className="bg-black/50 border-white/10 text-white min-h-[60px]" />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-white/70">Actual Behavior</label>
                <Textarea value={bugReport.actualBehavior} onChange={(e) => setBugReport({...bugReport, actualBehavior: e.target.value})} placeholder="What actually happens" className="bg-black/50 border-white/10 text-white min-h-[60px]" />
              </div>
            </div>
          </div>
          <DialogFooter className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setBugReportDialogOpen(false)} className="border-white/20 text-white hover:bg-white/10">Cancel</Button>
            <Button onClick={handleSubmitBugReport} disabled={bugReportSubmitting || !bugReport.title.trim() || !bugReport.description.trim()} className="bg-purple-600 hover:bg-purple-700" data-testid="submit-bug-report-btn">
              {bugReportSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>) : (<><Bug className="w-4 h-4 mr-2" />Submit Report</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adopt Conflict Dialog */}
      <Dialog open={!!adoptConflict} onOpenChange={(open) => !open && setAdoptConflict(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Replace Existing Organizers?</DialogTitle>
            <DialogDescription className="text-white/60">
              {adoptConflict?.message}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-3">
            <p className="text-white/50 text-sm">Your current organizers:</p>
            {adoptConflict?.existingSections?.map(s => (
              <div key={s.id} className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded text-sm text-white/70">
                <Layers className="w-4 h-4 text-purple-400" />
                {s.name}
              </div>
            ))}
            <p className="text-amber-400/80 text-xs mt-3">
              Replacing will remove your existing organizers and unassign all achievements from them.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setAdoptConflict(null)}
              className="border-white/10 text-white/70"
              data-testid="adopt-conflict-cancel"
            >
              Keep Existing
            </Button>
            <Button
              onClick={() => {
                if (adoptConflict.type === "organizer") {
                  handleAdoptOrganizer(adoptConflict.id, true);
                } else {
                  handleAdoptPlanner(adoptConflict.id, true);
                }
              }}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="adopt-conflict-replace"
            >
              Replace & Adopt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Community Item Dialog (for user's own items) */}
      <EditCommunityItemDialog
        open={!!editItem}
        onOpenChange={(open) => { if (!open) { setEditItem(null); setEditItemType(null); } }}
        item={editItem}
        itemType={editItemType}
        onSave={handleUserEditSave}
      />

      {/* Community Guidelines Dialog */}
      <GuidelinesDialog open={guidelinesOpen} onOpenChange={setGuidelinesOpen} />

      {/* Grid Template Preview Dialog */}
      <Dialog open={!!previewGrid} onOpenChange={(open) => !open && setPreviewGrid(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg max-h-[80vh] overflow-y-auto" data-testid="grid-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Grid3X3 className="w-5 h-5 text-purple-400" />
              {previewGrid?.name}
            </DialogTitle>
            {previewGrid?.description && (
              <DialogDescription className="text-white/50">{previewGrid.description}</DialogDescription>
            )}
          </DialogHeader>
          {previewGrid && (
            <div className="space-y-3">
              <div className="flex gap-3 text-white/40 text-xs">
                <span>{previewGrid.rows?.length || 0} rows x {previewGrid.cols?.length || 0} cols</span>
                <span>Used {previewGrid.useCount || 0} times</span>
                {previewGrid.submittedByUsername && <span>by {previewGrid.submittedByUsername}</span>}
              </div>
              <div className="overflow-x-auto">
                <table className="border-collapse w-full">
                  <thead>
                    <tr>
                      <th className="w-24" />
                      {(previewGrid.cols || []).map(c => (
                        <th key={c.id} className="px-1 py-1 text-center">
                          {c.isDivider ? (
                            <div className="w-[2px] h-4 mx-auto rounded" style={{ backgroundColor: c.color || "#3f3f46" }} />
                          ) : (
                            <span className={`text-xs font-medium ${c.name ? "text-zinc-300" : "text-zinc-600 italic"}`}>
                              {c.name || "Col"}
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(previewGrid.rows || []).map(r => {
                      if (r.isDivider) {
                        return (
                          <tr key={r.id}>
                            <td colSpan={(previewGrid.cols?.length || 0) + 1} className="py-0.5">
                              <div className="flex items-center gap-1">
                                <div className="flex-1 h-[2px] rounded" style={{ backgroundColor: r.color || "#3f3f46" }} />
                                {r.name && <span className="text-[10px]" style={{ color: r.color }}>{r.name}</span>}
                                <div className="flex-1 h-[2px] rounded" style={{ backgroundColor: r.color || "#3f3f46" }} />
                              </div>
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={r.id}>
                          <td className="pr-1 py-0.5">
                            <span className={`text-xs font-medium ${r.name ? "" : "italic opacity-50"}`} style={{ color: r.color }}>
                              {r.name || "Row"}
                            </span>
                          </td>
                          {(previewGrid.cols || []).map(c => {
                            if (c.isDivider) {
                              return (
                                <td key={c.id} className="px-0 py-0.5">
                                  <div className="w-[2px] h-6 mx-auto rounded" style={{ backgroundColor: c.color || "#3f3f46" }} />
                                </td>
                              );
                            }
                            const cell = previewGrid.cellDefaults?.[`${r.id}:${c.id}`];
                            const type = cell?.type || "checkbox";
                            return (
                              <td key={c.id} className="px-0.5 py-0.5 text-center">
                                <div className="w-full h-6 rounded border border-zinc-700/60 bg-zinc-900/40 flex items-center justify-center text-xs">
                                  {type === "checkbox" && (
                                    <span className={cell?.value ? "text-purple-400" : "text-zinc-700"}>{cell?.value ? "\u2713" : "\u2610"}</span>
                                  )}
                                  {type === "number" && (
                                    <span className="text-emerald-400 text-[11px]">{cell?.value || "0"}</span>
                                  )}
                                  {type === "symbol" && (
                                    <span className="text-amber-400">{cell?.value || "\u00B7"}</span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-white/40 text-xs text-center pt-2">
                To use this grid, go to any game page, click "Add Grid" and select it from "Community Templates".
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add-to-library prompt for adopting Maps/Checklists/Grids you don't own yet */}
      <Dialog open={!!addGamePrompt} onOpenChange={(open) => !open && setAddGamePrompt(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md" data-testid="adopt-add-game-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Gamepad2 className="w-5 h-5 text-purple-400" />
              Add game to your library?
            </DialogTitle>
            <DialogDescription className="text-white/50">
              You don't have <span className="text-white/80 font-medium">{addGamePrompt?.gameName}</span> in your library yet.
              Add it now and we'll adopt this item straight to it.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => setAddGamePrompt(null)}
              className="text-white/60 hover:text-white"
              data-testid="adopt-add-game-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddGameAndAdopt}
              className="bg-purple-600 hover:bg-purple-500 text-white"
              data-testid="adopt-add-game-confirm"
            >
              Add game &amp; adopt
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AdSense */}
      <AdBanner />
    </div>
  );
}
